import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, UsersIcon } from 'lucide-react';
interface CaseProps {
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Medium-Hard' | 'Hard';
  difficultyColor: string;
  tagline: string;
  story: string;
  bestFor: string;
  playTime: string;
  image: string;
  index: number;
}
function CaseCard({
  title,
  difficulty,
  difficultyColor,
  tagline,
  story,
  bestFor,
  playTime,
  image,
  index
}: CaseProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.15,
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-[#ece3d0] bg-paper-texture rounded shadow-2xl hover:shadow-[0_30px_60px_rgba(0,0,0,0.6)] transition-all duration-500 group flex flex-col border border-[#d4c5b0] overflow-hidden" data-id="element-58">
      {/* Dossier Cover Image */}
      <div className="relative h-44 sm:h-72 overflow-hidden bg-[#0a0a0a] border-b-2 border-[#1a1a1a]" data-id="element-59">
        <img src={image} alt={`Cover for ${title}`} className="w-full h-full object-cover opacity-70 group-hover:scale-110 group-hover:opacity-90 transition-all duration-1000 ease-out filter contrast-125 saturate-50" data-id="element-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-90" data-id="element-61" />
        <div className="absolute inset-0 bg-noise mix-blend-overlay opacity-30" data-id="element-62" />

        {/* Case File Stamp */}
        <div className="absolute top-4 right-4 border-2 border-[#8b1a1a]/80 text-[#8b1a1a]/80 font-typewriter text-sm font-bold px-3 py-1 rotate-[12deg] mix-blend-screen" data-id="element-63">
          CASE FILE
        </div>

        {/* Difficulty Badge */}
        <div className="absolute bottom-4 left-4 flex items-center space-x-2 bg-[#0a0a0a]/80 backdrop-blur-md px-3 py-1.5 border border-white/10 shadow-lg" data-id="element-64">
          <div className={`w-2.5 h-2.5 rounded-full ${difficultyColor} shadow-[0_0_10px_rgba(255,255,255,0.3)]`} data-id="element-65" />
          <span className="font-typewriter text-xs uppercase tracking-widest text-[#ece3d0]" data-id="element-66">
            {difficulty}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-5 sm:p-8 flex-1 flex flex-col relative" data-id="element-67">
        {/* Subtle tape detail */}
        <div className="absolute -top-3 right-8 w-16 h-6 bg-white/30 backdrop-blur-sm rotate-[3deg] shadow-sm z-10" data-id="element-68" />

        <div className="mb-4 sm:mb-6" data-id="element-69">
          <h3 className="font-typewriter text-xl sm:text-3xl text-[#1a1a1a] mb-1.5 sm:mb-2 leading-tight font-bold" data-id="element-70">
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-[#8b1a1a] font-typewriter tracking-wider uppercase" data-id="element-71">
            {tagline}
          </p>
        </div>

        <div className="border-t-2 border-dashed border-[#1a1a1a]/20 pt-4 sm:pt-6 flex-1" data-id="element-72">
          <p className="font-serif-body text-[#2a2a2a] leading-relaxed text-base sm:text-lg" data-id="element-73">
            {story}
          </p>
        </div>

        <div className="space-y-2 sm:space-y-3 pt-4 sm:pt-6 border-t-2 border-dashed border-[#1a1a1a]/20 mt-4 sm:mt-6" data-id="element-74">
          <div className="flex items-center space-x-3 text-sm text-[#1a1a1a]/80 font-serif-body text-base" data-id="element-75">
            <UsersIcon className="w-4 h-4 text-[#8b1a1a]" data-id="element-76" />
            <span data-id="element-77">
              <strong className="font-typewriter text-xs uppercase tracking-wider mr-2" data-id="element-78">
                Best for:
              </strong>{' '}
              {bestFor}
            </span>
          </div>
          <div className="flex items-center space-x-3 text-sm text-[#1a1a1a]/80 font-serif-body text-base" data-id="element-79">
            <ClockIcon className="w-4 h-4 text-[#8b1a1a]" data-id="element-80" />
            <span data-id="element-81">
              <strong className="font-typewriter text-xs uppercase tracking-wider mr-2" data-id="element-82">
                Play time:
              </strong>{' '}
              {playTime}
            </span>
          </div>
        </div>

        <button className="w-full bg-transparent border-2 border-[#1a1a1a] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#ece3d0] py-3 sm:py-4 font-typewriter font-bold tracking-wider transition-all mt-5 sm:mt-8 text-sm sm:text-base uppercase" data-id="element-83">
          $17 USD — Instant Download
        </button>
      </div>
    </motion.div>;
}
export function CaseCollection() {
  const cases: Omit<CaseProps, 'index'>[] = [{
    title: 'The Vanishing of Maple Street',
    difficulty: 'Easy',
    difficultyColor: 'bg-green-500',
    tagline: 'Perfect for first timers',
    story: "In 1987, an entire family vanished from their home in a quiet Ohio suburb. Dinner was on the table. The TV was on. No one saw anything. No one heard anything. The case was never solved. Can you do what the police couldn't?",
    bestFor: 'Couples, families, first-time players',
    playTime: '90 minutes',
    image: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=1000&auto=format&fit=crop'
  }, {
    title: 'The Blackwood Inheritance',
    difficulty: 'Medium',
    difficultyColor: 'bg-yellow-500',
    tagline: "Things aren't what they seem",
    story: "When millionaire Richard Blackwood was found dead in his Louisiana estate, police ruled it a suicide in under 48 hours. His daughter never believed it. Twenty years later, the case files were leaked. Now they're in your hands. The fortune didn't disappear on its own. Someone took it. Who?",
    bestFor: 'Friends who love a plot twist',
    playTime: '2 hours',
    image: 'https://images.unsplash.com/photo-1574180045827-681f8a1a9622?q=80&w=1000&auto=format&fit=crop'
  }, {
    title: 'Frequency: The Last Broadcast',
    difficulty: 'Medium-Hard',
    difficultyColor: 'bg-orange-500',
    tagline: 'Listen carefully',
    story: 'In 1994, an investigative journalist in Nevada disappeared hours after recording what he called "the story that would change everything." The recording exists. It\'s incomplete. There are hidden messages in it. And someone doesn\'t want them found.',
    bestFor: 'Groups who want a full challenge',
    playTime: '2.5 to 3 hours',
    image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=1000&auto=format&fit=crop'
  }, {
    title: 'The Hollow Man Files',
    difficulty: 'Hard',
    difficultyColor: 'bg-red-600',
    tagline: 'Not for the faint hearted',
    story: "For 11 years, a serial killer operated across three different states without leaving an identifiable trace. The FBI closed the case in 2001. The files were classified. Someone leaked them. Now you have what federal agents couldn't solve. Not everyone who looks for the truth is ready to find it.",
    bestFor: 'Experienced groups ready for a real challenge',
    playTime: '3 to 4 hours',
    image: 'https://images.unsplash.com/photo-1626265774643-f1943311a86b?q=80&w=1000&auto=format&fit=crop'
  }];
  return <section id="collection" className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a] relative" data-id="element-84">
      <div className="absolute inset-0 bg-noise opacity-20 mix-blend-overlay pointer-events-none" data-id="element-85" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-86">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-10 sm:mb-20" data-id="element-87">
          <h2 className="font-typewriter text-3xl sm:text-5xl lg:text-6xl text-[#ece3d0] mb-3 sm:mb-6 drop-shadow-lg" data-id="element-88">
            The Collection
          </h2>
          <p className="font-serif-body text-[#ece3d0]/70 text-base sm:text-xl max-w-2xl mx-auto" data-id="element-89">
            Four cases. Four levels of intensity. Months of unforgettable
            nights.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12" data-id="element-90">
          {cases.map((caseData, index) => <CaseCard key={caseData.title} {...caseData} index={index} data-id="element-91" />)}
        </div>
      </div>
    </section>;
}